import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChargeService } from '../../services/charge.service';
import {
  ForecastCollectionCalendarService,
  mapCollectionCalendar,
  type CollectionCalendarDayBucket,
  type CollectionCalendarKpis
} from '../../services/forecast-collection-calendar.service';
import {
  ChargeOperationalStatusEnum,
  CollectionMethodEnum,
  type ChargeDTO,
  type ChargeOperationalStatusEnumValue,
  type CollectionMethodEnumValue,
  type CustomerDTO,
  type InvoiceDTO,
  type SaleDTO
} from '../../core/models';
import type { SearchChargeFilterSpec } from '../../services/service-common';
import { ChargeReadComponent } from './charge-read.component';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { TableModule } from 'primeng/table';

type ChargeStatus = ChargeOperationalStatusEnumValue | string;

interface CalendarDay {
  date: Date;
  key: string;
  inMonth: boolean;
  isToday: boolean;
  chargeCount: number;
  totalAmount: number;
}

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

@Component({
  selector: 'app-charges-calendar',
  standalone: true,
  templateUrl: './charges-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .day-charges-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    `
  ],
  imports: [
    CommonModule,
    ChargeReadComponent,
    ButtonModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
    DrawerModule,
    TableModule
  ]
})
export class ChargesCalendarComponent {
  private forecastService = inject(ForecastCollectionCalendarService);
  private chargeService = inject(ChargeService);

  readonly weekdayLabels = WEEKDAY_LABELS;
  readonly pageSizeOptions = [10, 20, 50];

  viewDate = signal(startOfMonth(new Date()));
  daySummaries = signal<Map<string, CollectionCalendarDayBucket>>(new Map());
  kpis = signal<CollectionCalendarKpis>({ chargeCount: 0, totalAmount: 0 });
  loading = signal(false);
  error = signal<string | null>(null);

  selectedDay = signal<CalendarDay | null>(null);
  isDayDrawerOpen = signal(false);
  dayCharges = signal<ChargeDTO[]>([]);
  dayChargesLoading = signal(false);
  dayPage = signal(0);
  dayPageSize = signal(20);
  dayTotalItems = signal(0);

  selectedCharge = signal<ChargeDTO | null>(null);
  isDetailsOpen = signal(false);

  monthTitle = computed(() => {
    const d = this.viewDate();
    return `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
  });

  selectedDayTitle = computed(() => {
    const day = this.selectedDay();
    if (!day) return '';
    return day.date.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  });

  days = computed<CalendarDay[]>(() => {
    const monthStart = this.viewDate();
    const summaries = this.daySummaries();
    const todayKey = toDateKey(new Date());
    return buildMonthGrid(monthStart).map((date) => {
      const key = toDateKey(date);
      const summary = summaries.get(key);
      return {
        date,
        key,
        inMonth: date.getMonth() === monthStart.getMonth() && date.getFullYear() === monthStart.getFullYear(),
        isToday: key === todayKey,
        chargeCount: summary?.chargeCount ?? 0,
        totalAmount: summary?.totalAmount ?? 0
      };
    });
  });

  constructor() {
    this.loadMonth();
  }

  previousMonth(): void {
    const d = this.viewDate();
    this.viewDate.set(new Date(d.getFullYear(), d.getMonth() - 1, 1));
    this.loadMonth();
  }

  nextMonth(): void {
    const d = this.viewDate();
    this.viewDate.set(new Date(d.getFullYear(), d.getMonth() + 1, 1));
    this.loadMonth();
  }

  goToCurrentMonth(): void {
    this.viewDate.set(startOfMonth(new Date()));
    this.loadMonth();
  }

  retryLoad(): void {
    this.loadMonth();
  }

  openDaySummary(day: CalendarDay): void {
    if (day.chargeCount <= 0) return;
    this.selectedDay.set(day);
    this.dayPage.set(0);
    this.dayCharges.set([]);
    this.dayTotalItems.set(day.chargeCount);
    this.isDayDrawerOpen.set(true);
    this.loadDayCharges();
  }

  onDayDrawerVisibleChange(visible: boolean): void {
    this.isDayDrawerOpen.set(visible);
    if (!visible) {
      this.selectedDay.set(null);
      this.dayCharges.set([]);
      this.dayTotalItems.set(0);
    }
  }

  onDayLazyLoad(event: { first: number; rows: number }): void {
    const page = event.rows ? Math.floor(event.first / event.rows) : 0;
    const size = event.rows || this.dayPageSize();
    if (!this.isDayDrawerOpen() || !this.selectedDay()) return;
    if (page === this.dayPage() && size === this.dayPageSize()) return;
    this.dayPage.set(page);
    this.dayPageSize.set(size);
    this.loadDayCharges();
  }

  openChargeDetails(charge: ChargeDTO): void {
    if (!charge?.id) return;
    this.selectedCharge.set(charge);
    this.isDetailsOpen.set(true);
  }

  onChargeDrawerVisibleChange(visible: boolean): void {
    this.isDetailsOpen.set(visible);
    if (!visible) this.selectedCharge.set(null);
  }

  getChargeSale(charge: ChargeDTO | null | undefined): SaleDTO | undefined {
    return charge?.sale ?? undefined;
  }

  getChargeInvoice(charge: ChargeDTO | null | undefined): InvoiceDTO | undefined {
    return charge?.sale?.invoice ?? charge?.invoice ?? undefined;
  }

  getChargeCustomer(charge: ChargeDTO | null | undefined): CustomerDTO | undefined {
    return charge?.sale?.customer ?? charge?.invoice?.customer ?? undefined;
  }

  getStatusLabel(status: ChargeStatus | undefined): string {
    switch (status) {
      case ChargeOperationalStatusEnum.PENDING:
        return 'Pendiente';
      case ChargeOperationalStatusEnum.DUE:
        return 'Vencido';
      case ChargeOperationalStatusEnum.SCHEDULED:
        return 'Programado';
      case ChargeOperationalStatusEnum.PAID:
        return 'Pagado';
      case ChargeOperationalStatusEnum.FAILED:
        return 'Fallido';
      case ChargeOperationalStatusEnum.EXPIRED:
        return 'Expirado';
      case ChargeOperationalStatusEnum.CANCELLED:
        return 'Cancelado';
      default:
        return status || '—';
    }
  }

  getStatusSeverity(status: ChargeStatus | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | null {
    if (status === ChargeOperationalStatusEnum.PAID) return 'success';
    if (status === ChargeOperationalStatusEnum.PENDING || status === ChargeOperationalStatusEnum.SCHEDULED) return 'info';
    if (status === ChargeOperationalStatusEnum.DUE) return 'warn';
    if (status === ChargeOperationalStatusEnum.CANCELLED || status === ChargeOperationalStatusEnum.FAILED) return 'danger';
    return 'secondary';
  }

  getStatusIcon(status: ChargeStatus | undefined): string {
    switch (status) {
      case ChargeOperationalStatusEnum.PAID:
        return 'pi pi-check-circle';
      case ChargeOperationalStatusEnum.PENDING:
      case ChargeOperationalStatusEnum.SCHEDULED:
        return 'pi pi-clock';
      case ChargeOperationalStatusEnum.DUE:
        return 'pi pi-exclamation-circle';
      case ChargeOperationalStatusEnum.CANCELLED:
        return 'pi pi-times-circle';
      case ChargeOperationalStatusEnum.FAILED:
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-minus-circle';
    }
  }

  getStatusIconClasses(status: ChargeStatus | undefined): string {
    const base = 'shrink-0 h-8 w-8 rounded-full flex items-center justify-center';
    switch (status) {
      case ChargeOperationalStatusEnum.PAID:
        return `${base} bg-green-100 text-green-600`;
      case ChargeOperationalStatusEnum.PENDING:
      case ChargeOperationalStatusEnum.SCHEDULED:
        return `${base} bg-sky-100 text-sky-600`;
      case ChargeOperationalStatusEnum.DUE:
        return `${base} bg-amber-100 text-amber-600`;
      case ChargeOperationalStatusEnum.CANCELLED:
      case ChargeOperationalStatusEnum.FAILED:
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getCollectionMethodLabel(method: CollectionMethodEnumValue | string | null | undefined): string {
    if (!method) return '—';
    const labels: Record<string, string> = {
      [CollectionMethodEnum.LINK]: 'Liga de pago',
      [CollectionMethodEnum.MANUAL]: 'Manual',
      [CollectionMethodEnum.RECURRING_CHARGE]: 'Cobro recurrente',
      [CollectionMethodEnum.MERCHANT_INITIATED]: 'Cobro bajo demanda'
    };
    return labels[method] ?? String(method);
  }

  private loadMonth(): void {
    this.loading.set(true);
    this.error.set(null);
    const monthStart = this.viewDate();
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const from = toDateKey(monthStart);
    const to = toDateKey(monthEnd);

    this.forecastService.getCalendar(from, to).subscribe({
      next: (response) => {
        if (!response.ok || !response.body) {
          this.error.set(response.message || 'Error al cargar el calendario de cobranza');
          this.daySummaries.set(new Map());
          this.kpis.set({ chargeCount: 0, totalAmount: 0 });
          this.loading.set(false);
          return;
        }
        const mapped = mapCollectionCalendar(response.body);
        const map = new Map<string, CollectionCalendarDayBucket>();
        for (const day of mapped.days) {
          if (day.date) map.set(day.date, day);
        }
        this.daySummaries.set(map);
        this.kpis.set(mapped.kpis);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.daySummaries.set(new Map());
        this.kpis.set({ chargeCount: 0, totalAmount: 0 });
        this.loading.set(false);
      }
    });
  }

  private loadDayCharges(): void {
    const day = this.selectedDay();
    if (!day) return;
    this.dayChargesLoading.set(true);
    const spec: SearchChargeFilterSpec = {
      'dueDate[bw]': [toLocalDateStartIso(day.date), toLocalDateEndIso(day.date)]
    };
    this.chargeService
      .searchCharges(spec, {
        page: this.dayPage(),
        size: this.dayPageSize(),
        sort: ['total,desc']
      })
      .subscribe({
        next: (response) => {
          this.dayCharges.set(response.body ?? []);
          this.dayTotalItems.set(response.page?.totalItems ?? response.body?.length ?? 0);
          this.dayChargesLoading.set(false);
        },
        error: () => {
          this.dayCharges.set([]);
          this.dayTotalItems.set(0);
          this.dayChargesLoading.set(false);
        }
      });
  }
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildMonthGrid(monthStart: Date): Date[] {
  const firstWeekday = (monthStart.getDay() + 6) % 7;
  const gridStart = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1 - firstWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    return new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
  });
}

function toLocalDateStartIso(date: Date): string {
  return `${toDateKey(date)}T00:00:00.000Z`;
}

function toLocalDateEndIso(date: Date): string {
  return `${toDateKey(date)}T23:59:59.999Z`;
}
